// components/tour/tourStore.js
//
// Tiny external store coordinating the guided tour with the rest of the app
// (the routed pages live outside the AppShell/TourHost subtree). Modeled on
// swa-botfieldPM's lib/tourDemo.ts. Three concerns:
//   • control — what the tour is doing: 'idle' | 'menu' (chooser open) | 'running'
//               (+ which track). Driven by the "?" button, the navbot ✨ button,
//               and the first-visit offer; consumed by <TourHost>.
//   • demo    — while true, pages render curated VIEW-ONLY sample data
//               (tourDemoData) so every data-dependent stop looks populated and
//               nothing the user sees persists.
//   • drive   — a directive string telling the active page to open a specific
//               modal/panel on the seeded data (e.g. 'grid-edit-entry'). Keyed
//               on the string so it survives consecutive sub-steps.
//
// Archivable: deleting this file + tourDemoData.js + the call-site demo branches
// removes the tour entirely.

import { useSyncExternalStore } from 'react';

// 'idle' = nothing showing; 'menu' = chooser popover; 'running' = a tour track.
let control = { phase: 'idle', trackKey: null };
let demoOn = false;
let drive = null;

const subscribers = new Set();
const emit = () => subscribers.forEach((fn) => fn());
const subscribe = (fn) => { subscribers.add(fn); return () => { subscribers.delete(fn); }; };

export const tourStore = {
  // ── control ──
  openMenu() { control = { phase: 'menu', trackKey: null }; emit(); },
  // Enable demo-mode up front so the first page the tour navigates to mounts
  // already seeded (no flash of the real/empty page under the overlay).
  start(trackKey) { control = { phase: 'running', trackKey }; demoOn = true; emit(); },
  close() {
    if (control.phase === 'idle' && !demoOn && drive === null) return;
    control = { phase: 'idle', trackKey: null };
    demoOn = false;
    drive = null;
    emit();
  },
  getControl() { return control; },

  // ── demo ──
  setDemo(value) { if (demoOn !== value) { demoOn = value; emit(); } },
  getDemo() { return demoOn; },

  // ── drive ──
  setDrive(directive) {
    const v = directive == null ? null : directive;
    if (drive !== v) { drive = v; emit(); }
  },
  getDrive() { return drive; },

  subscribe,
};

export function useTourControl() {
  return useSyncExternalStore(subscribe, tourStore.getControl, () => control);
}
export function useTourDemo() {
  return useSyncExternalStore(subscribe, tourStore.getDemo, () => false);
}
export function useTourDrive() {
  return useSyncExternalStore(subscribe, tourStore.getDrive, () => null);
}
